import {
  BrowseAndSelectTokenButton,
  DropDownSelectBoxOption,
  InfoAlert,
  TokenSelectionDropDownSelectBox,
} from '@/components';
import { useTradeableTokens } from '@/components/providers/postauth/TradeableTokensProvider';
import APP_CONFIG from '@/config/app';
import { TokenInterface } from '@/interfaces';
import { TRADE_CARD } from '@/utils/strings';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
export interface BuyableAssetType extends TokenInterface {}
interface Props {
  selectedAsset: BuyableAssetType | null;
  selectedSellBlockchain?: string | null;
  handleSelectChange: (asset: BuyableAssetType) => void;
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  clearErrors: UseFormClearErrors<any>;
  errors: FieldErrors;
}
const BuyTokenDropDownSelectBox: FC<Props> = ({
  selectedAsset,
  selectedSellBlockchain,
  handleSelectChange,
  register,
  setValue,
  clearErrors,
  errors,
}) => {
  const { getFeaturedTokens, getTokens } = useTradeableTokens();
  const supportedCrossChainBlockchains = useMemo(
    () => ['ethereum', 'base'],
    [],
  );
  const prevSelectedAssetRef = useRef<BuyableAssetType | null>(selectedAsset);
  const [preventClose, setPreventClose] = useState(false);
  const [closeDropdown, setCloseDropdown] = useState(false);
  const tokensToBeListedAsOptions = useMemo(() => {
    const tokenCount = APP_CONFIG.BUY_TOKEN_DROPDOWN_FEATURED_TOKENS_COUNT;
    const normalizedSellBlockchain = selectedSellBlockchain?.toLowerCase();
    const normalizeBlockchain = (blockchain?: string | null) =>
      typeof blockchain === 'string' ? blockchain.toLowerCase() : '';
    const isCrossChainSell =
      normalizedSellBlockchain &&
      supportedCrossChainBlockchains.includes(normalizedSellBlockchain);
    const allowedBlockchains = normalizedSellBlockchain
      ? isCrossChainSell
        ? supportedCrossChainBlockchains
        : [normalizedSellBlockchain]
      : APP_CONFIG.TRADE_SUPPORTED_NETWORKS;
    const filterByAllowedBlockchains = (token: TokenInterface) => {
      const tokenBlockchain = normalizeBlockchain(token?.blockchain);
      return tokenBlockchain
        ? allowedBlockchains.includes(tokenBlockchain)
        : false;
    };
    let tokens =
      getFeaturedTokens(tokenCount).filter(filterByAllowedBlockchains) || [];
    if (tokens.length === 0) {
      const allTokens = getTokens();
      tokens = allTokens
        .filter(filterByAllowedBlockchains)
        .sort((a, b) => (b.marketCapInUsd || 0) - (a.marketCapInUsd || 0))
        .slice(0, tokenCount);
    }
    if (
      selectedAsset &&
      filterByAllowedBlockchains(selectedAsset) &&
      !tokens.some((t) => t.id === selectedAsset.id)
    ) {
      tokens = [selectedAsset, ...tokens.slice(0, tokenCount - 1)];
    }
    return tokens;
  }, [
    getFeaturedTokens,
    getTokens,
    selectedAsset,
    selectedSellBlockchain,
    supportedCrossChainBlockchains,
  ]);
  const handleTokenSelectionInBrowseAndSelectTokenTray = useCallback(
    (token: TokenInterface) => {
      handleSelectChange(token as BuyableAssetType);
      setValue('buyAsset', token, { shouldValidate: true });
      clearErrors('buyAsset');
      setPreventClose(false);
      setCloseDropdown(true);
    },
    [
      handleSelectChange,
      setValue,
      clearErrors,
      setPreventClose,
      setCloseDropdown,
    ],
  );
  const handleAssetSelect = useCallback(
    (
      param:
        | React.ChangeEvent<HTMLInputElement>
        | DropDownSelectBoxOption<string>,
    ) => {
      if ('value' in param) {
        const selectedToken = tokensToBeListedAsOptions.find(
          (token) => token.id === param.value,
        );
        if (selectedToken) {
          handleSelectChange(selectedToken as BuyableAssetType);
          clearErrors('buyAsset');
          setCloseDropdown(true);
        }
      }
    },
    [handleSelectChange, clearErrors, tokensToBeListedAsOptions],
  );
  const handleBrowseAndSelectClick = useCallback(() => {
    setPreventClose(true);
    setCloseDropdown(false);
  }, [setPreventClose, setCloseDropdown]);
  useEffect(() => {
    if (prevSelectedAssetRef.current !== selectedAsset) {
      setPreventClose(false);
      setCloseDropdown(true);
      prevSelectedAssetRef.current = selectedAsset;
    }
  }, [selectedAsset]);
  return (
    <TokenSelectionDropDownSelectBox
      name="buyAsset"
      label={TRADE_CARD.SCREENS.TRADE.LABELS.BUY_ASSET_SELECTION_FIELD.LABEL}
      tokenToInvest={selectedAsset || undefined}
      tokensToBeListedAsOptions={tokensToBeListedAsOptions}
      onTokenSelect={handleAssetSelect}
      register={register}
      setValue={setValue}
      clearErrors={clearErrors}
      errors={errors}
      preventCloseOnOutsideClick={preventClose}
      closeDropdown={closeDropdown}
      tooltip={
        <InfoAlert
          title={
            TRADE_CARD.SCREENS.TRADE.LABELS.BUY_ASSET_SELECTION_FIELD
              .TOOLTIP_TITLE
          }
          description={
            TRADE_CARD.SCREENS.TRADE.LABELS.BUY_ASSET_SELECTION_FIELD
              .TOOLTIP_DESCRIPTION
          }
          intent="transparent"
        />
      }
      tooltipIsFullBleed
      extraButton={
        <BrowseAndSelectTokenButton
          onClick={handleBrowseAndSelectClick}
          setPreventBrowseAndSelectTokenTrayFromClosing={setPreventClose}
          onTokenSelectInBrowseAndSelectTokenTray={
            handleTokenSelectionInBrowseAndSelectTokenTray
          }
          blockchains={
            selectedSellBlockchain
              ? supportedCrossChainBlockchains.includes(
                  selectedSellBlockchain.toLowerCase(),
                )
                ? supportedCrossChainBlockchains
                : [selectedSellBlockchain].filter(Boolean)
              : APP_CONFIG.TRADE_SUPPORTED_NETWORKS
          }
        />
      }
      extraButtonPosition="bottom"
    />
  );
};
export default React.memo(BuyTokenDropDownSelectBox);
